import React, { useEffect, useMemo, useState } from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import qs from 'query-string'

import CheckoutLayout from '../components/CheckoutLayout'
import LicenseCounter from '../components/LicenseCounter'
import OrderSummary from '../components/OrderSummary'
import useBillingInfo from '../services/useBillingInfo'
import useOrganization from '../services/useOrganization'
import usePlan from '../services/usePlan'
import usePublicPlans from '../services/usePublicPlans'
import useWorkspaceId from '../services/useWorkspaceId'
import { IOrganization, IPlan, Subscription, PlanPeriod, ICustomer } from '../types'
import ChangePlan from './ChangePlan'
import Checkout from './Checkout'

export default () => {
  const organization = useOrganization()
  const workspaceId = useWorkspaceId()
  const { subscription, customer } = useBillingInfo()
  const location = useLocation()
  const plans = usePublicPlans()
  const { planName, planPeriod } = qs.parse(location.search)

  return (
    <CheckoutRouter
      plans={plans}
      targetPlanName={planName ? String(planName) : null}
      targetPlanPeriod={planPeriod ? (String(planPeriod) as PlanPeriod) : null}
      organization={organization}
      subscription={subscription}
      workspaceId={workspaceId}
      customer={customer}
    />
  )
}

export function CheckoutRouter(props: {
  plans: Array<IPlan> | undefined
  targetPlanName: string | null
  targetPlanPeriod: PlanPeriod | null
  organization: IOrganization | undefined
  subscription: Subscription | null | undefined
  workspaceId: string
  customer: ICustomer | null | undefined
}): React.ReactElement {
  const [frozenSubscription, setFrozenSubscription] = useState(props.subscription)

  const targetPlan = usePlan(props.targetPlanName)
  const targetPlanAndPeriod = useMemo(() => {
    if (targetPlan === undefined) {
      return undefined
    }

    if (targetPlan === null || !props.targetPlanPeriod) {
      return null
    }

    const period = targetPlan.periods?.[props.targetPlanPeriod]
    if (period && 'id' in period && 'price' in period) {
      return {
        id: period.id,
        name: targetPlan.name,
        price: period.price,
        currency: targetPlan.currency,
        period: props.targetPlanPeriod,
        minimumUsers: targetPlan.minimumUsers,
        isFree: targetPlan.isFree,
      }
    }

    return null
  }, [targetPlan, props.targetPlanPeriod])

  // Prevent accidentally going to `/summary` page after creating a new subscription
  // as the success pages must be displayed beforehand
  useEffect(() => {
    if (props.subscription !== undefined && frozenSubscription === undefined) {
      setFrozenSubscription(props.subscription)
    }
  }, [props.subscription])

  if (
    props.plans === undefined ||
    props.organization === undefined ||
    frozenSubscription === undefined ||
    targetPlanAndPeriod === undefined
  ) {
    return (
      <div>
        <CheckoutLayout left={<LicenseCounter />} right={<OrderSummary />} />
      </div>
    )
  }

  if (targetPlanAndPeriod === null) {
    return (
      <Navigate
        replace
        to={{
          pathname: '/plans',
          search: qs.stringify({ workspaceId: props.workspaceId }),
        }}
      />
    )
  }

  const currentSubscriptionPlanName = frozenSubscription?.currentPlan.name
  const isSamePlan = currentSubscriptionPlanName === targetPlanAndPeriod.name

  if (isSamePlan) {
    return (
      <Navigate
        replace
        to={{
          pathname: '/summary',
          search: qs.stringify({ workspaceId: props.workspaceId }),
        }}
      />
    )
  }

  if (frozenSubscription === null) {
    return <Checkout organization={props.organization} plan={targetPlanAndPeriod} />
  }

  return (
    <ChangePlan
      targetPlan={targetPlanAndPeriod}
      organization={props.organization}
      subscription={frozenSubscription}
    />
  )
}
